import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Creative,
  AssignedLabelsResponse,
  Platform,
  AssignedLabelItem,
  VideoRetentionData,
  CreativeReportColumn,
} from "../../types";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useGlobalStore } from "@/GlobalStore";
import { getAssignedLabels, updateAssignedLabel } from "../../apiService";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { LuCircleCheck, LuLoaderCircle } from "react-icons/lu";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import AdCard from "@/components/common/adCard";
import { getPlatformIcon } from "@/screens/utils/Helpers";
import { SqButton } from "@/components/squircle-ui/button";
import { RiCloseLine, RiSearchLine } from "@remixicon/react";
import OptionsAction from "@/components/common/adCard/actions/OptionsAction";

// LabelManagement component moved to its own file

interface CreativeGalleryPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creatives: (Creative & {
    type: "image" | "video" | "mixed";
    ad_name: string;
    ad_id?: string;
    creative_id?: string;
    videoRetentionData?: VideoRetentionData;
  })[];
  groupType?: string;
  currentGroup?: string | null;
  creativeType?: string;
  labelView?: string;
  labelConfigId?: string;
  accountId?: string;
  disableLabelManagement?: boolean;
  enableSearch?: boolean;
  ad_columns?: CreativeReportColumn[];
  loading?: boolean;
  /** When set with onLoadMore, enables scroll-based API pagination (infinite scroll). */
  totalCreatives?: number;
  /** Called when user scrolls near bottom and creatives.length < totalCreatives. */
  onLoadMore?: () => Promise<void>;
}

const CreativeGalleryPreview = ({
  open,
  onOpenChange,
  creatives,
  groupType,
  currentGroup,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  creativeType,
  labelView,
  labelConfigId,
  accountId,
  disableLabelManagement,
  enableSearch,
  ad_columns,
  loading,
  totalCreatives,
  onLoadMore,
}: CreativeGalleryPreviewProps) => {
  const [gridCols, setGridCols] = useState(4);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [labelData, setLabelData] = useState<AssignedLabelsResponse | null>(null);
  const [creativesState, setCreativesState] = useState<typeof creatives>([]);
  const ITEMS_PER_PAGE = 12; // Number of items to load on each scroll (client-side mode)
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE); // Initial number of items to show
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);

  const apiPaginationMode = totalCreatives != null && typeof onLoadMore === "function";

  // searching states
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Initialize creativesState when the component mounts or when creatives prop changes
  useEffect(() => {
    if (open && creatives.length > 0) {
      setCreativesState(creatives);
      setDisplayedCount(ITEMS_PER_PAGE); // Reset pagination when creatives change
    } else if (!open) {
      // Clear immediately on close
      setCreativesState([]);
      setDisplayedCount(ITEMS_PER_PAGE);
    }
  }, [creatives, open]);

  // Clear creativesState and reset pagination when dialog closes
  useEffect(() => {
    if (!open) {
      setCreativesState([]);
      setDisplayedCount(ITEMS_PER_PAGE);
      setIsLoading(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
      setSearchQuery("");
      setIsSearching(false);
    }
  }, [open]);

  // Reset scroll position when search query changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [searchQuery]);

  // Fetch labels when component mounts and when labelConfigId changes
  // Defer label fetching to avoid blocking dialog open
  useEffect(() => {
    if (!open) return;

    const fetchLabels = async () => {
      if (!accountId || !labelConfigId || groupType !== "text_ai_label") return;
      try {
        // Defer API call until after dialog is visible
        await new Promise((resolve) => setTimeout(resolve, 100));
        const response = await getAssignedLabels(accountId, labelConfigId);
        setLabelData(response);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    };

    // Defer label fetching until after dialog animation
    requestAnimationFrame(() => {
      void fetchLabels();
    });
  }, [accountId, groupType, labelConfigId, open]);

  // Unified handler for updating labels (single or multiple) with full label objects
  const handleLabelsUpdate = async (adId: string, labels: AssignedLabelItem[]) => {
    if (!accountId || !labelConfigId || groupType !== "text_ai_label") return;
    try {
      const targetCreative = creativesState.find((creative) => creative.ad_id === adId);
      if (!targetCreative) return;

      const response = await updateAssignedLabel(accountId, labelConfigId, {
        ad_id: adId,
        labels,
      });

      setLabelData((prev) =>
        prev ? { ...prev, assigned_labels: response.assigned_labels } : null,
      );

      // If the new labels do not include the current group, remove the creative
      if (currentGroup && !labels.some((l) => l.label === currentGroup)) {
        const updatedCreatives = creativesState.filter(
          (creative) => creative.creative_id !== targetCreative.creative_id,
        );
        setCreativesState(updatedCreatives);
        if (updatedCreatives.length === 0) {
          onOpenChange(false);
        }
      }

      // Additionally, for video sequence edits (any labelView set), remove the creative after save
      if (labelView === "sequence" && targetCreative.type === "video") {
        const updatedCreatives = creativesState.filter(
          (creative) => creative.creative_id !== targetCreative.creative_id,
        );
        setCreativesState(updatedCreatives);
        if (updatedCreatives.length === 0) {
          onOpenChange(false);
        }
      }

      // Emit custom event with the specific labelConfigId
      const event = new CustomEvent("labelUpdated", {
        detail: { labelConfigId },
      });
      window.dispatchEvent(event);

      toast.message(
        <ToastContent
          icon={LuCircleCheck}
          headingPrefix={`Labels updated`}
          subheadingPrefix="Ad labels updated successfully."
        />,
      );
    } catch (error) {
      console.error("Error updating labels:", error);
    }
  };

  // Get current label items for an ad (new API shape only)

  const getCurrentLabelItems = (adId: string) => {
    if (!labelData) return [] as AssignedLabelItem[];
    const entry = labelData.assigned_labels.find((l) => l.ad_id === adId);
    if (!entry) return [] as AssignedLabelItem[];
    return entry.labels;
  };

  // Get selected account for winning criteria
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);

  // Helper function to calculate winning criteria for a creative
  const getWinningCriteriaForCreative = useCallback(
    (creative: (typeof creatives)[0] | Record<string, unknown>) => {
      const winningFilters = selectedAccount?.additional_info?.winning_filters;
      if (!winningFilters || !ad_columns) {
        return [];
      }

      // Find winning criteria columns
      const winningColumns = ad_columns.filter(
        (col) =>
          (col as CreativeReportColumn & { winning_criteria_name?: string })
            .winning_criteria_name,
      );

      // Get winning criteria data
      const winningCriteriaData = winningColumns
        .filter((col) => {
          const value = (creative as Record<string, unknown>)[col.key];
          return value === true;
        })
        .map((col) => {
          const winningCriteriaName = (
            col as CreativeReportColumn & {
              winning_criteria_name?: string;
            }
          ).winning_criteria_name;
          if (!winningCriteriaName) return null;

          // Find matching criteria in winning_filters
          const criteria = winningFilters
            .flatMap((section) => section.criterias)
            .find((c) => c.criteria_name === winningCriteriaName);

          const colorCode = (
            criteria as { criteria_name: string; color_code?: string } | undefined
          )?.color_code;

          return {
            criteriaName: winningCriteriaName,
            colorCode,
          };
        })
        .filter(
          (item): item is { criteriaName: string; colorCode: string } =>
            item !== null && Boolean(item.colorCode),
        );

      return winningCriteriaData;
    },
    [ad_columns, selectedAccount],
  );

  useEffect(() => {
    if (!open) return;

    const updateGridCols = () => {
      if (!containerRef.current || !cardRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const cardWidth = cardRef.current.offsetWidth;
      const fixedGap = 14;

      // Calculate columns with fixed gap
      const possibleCols = Math.floor(containerWidth / (cardWidth + fixedGap));

      // Clamp between 2 and 4 columns
      const newCols = Math.min(Math.max(possibleCols, 2), 4);
      setGridCols(newCols);
    };

    // Defer grid calculation until after dialog animation
    // Use double RAF to ensure DOM is fully ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateGridCols();
      });
    });

    const resizeObserver = new ResizeObserver(() => {
      // Throttle resize updates
      requestAnimationFrame(updateGridCols);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Throttle window resize
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        requestAnimationFrame(updateGridCols);
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [open]);

  // Filter creatives based on search query
  const filteredCreatives = searchQuery.trim()
    ? creativesState.filter((creative) =>
        creative.ad_name?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : creativesState;

  // Handle scroll pagination (client-side reveal or API load more)
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const scrollThreshold = 400; // Load more when 400px from bottom

      if (scrollTop + clientHeight < scrollHeight - scrollThreshold) return;

      if (apiPaginationMode) {
        if (isLoadingMore || isLoadingMoreRef.current) return;
        const hasMore = creativesState.length < (totalCreatives ?? 0);
        if (!hasMore || !onLoadMore) return;
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        void onLoadMore().finally(() => {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        });
        return;
      }

      if (isLoading) return;
      if (displayedCount < filteredCreatives.length) {
        setIsLoading(true);
        setTimeout(() => {
          setDisplayedCount((prev) =>
            Math.min(prev + ITEMS_PER_PAGE, filteredCreatives.length),
          );
          setIsLoading(false);
        }, 100);
      }
    },
    [
      apiPaginationMode,
      creativesState.length,
      displayedCount,
      filteredCreatives.length,
      isLoading,
      isLoadingMore,
      onLoadMore,
      totalCreatives,
    ],
  );

  // Get the displayed subset: all filtered in API mode, else client-side slice
  const displayedCreatives = useMemo(
    () =>
      apiPaginationMode ? filteredCreatives : filteredCreatives.slice(0, displayedCount),
    [apiPaginationMode, filteredCreatives, displayedCount],
  );

  // Organize data into columns for masonry layout - memoized and optimized
  const columnData = useMemo(() => {
    if (displayedCreatives.length === 0) return [];

    // Use minimum between items length and gridCols to avoid empty columns
    const actualCols = Math.min(displayedCreatives.length, gridCols);
    const cols: Array<
      Array<
        Creative & {
          type: "image" | "video" | "mixed";
          ad_name: string;
          ad_id?: string;
          videoRetentionData?: VideoRetentionData;
        }
      >
    > = [];

    // Pre-allocate columns
    for (let i = 0; i < actualCols; i++) {
      cols[i] = [];
    }

    // Use for loop for better performance
    for (let i = 0; i < displayedCreatives.length; i++) {
      const colIndex = i % actualCols;
      cols[colIndex].push(displayedCreatives[i]);
    }

    return cols;
  }, [displayedCreatives, gridCols]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-full max-h-full bg-transparent border-0 px-0 gap-0 py-0 outline-none shadow-none flex ${enableSearch && isSearching ? "flex-col" : "flex-row"}`}
        style={enableSearch ? { top: "0", transform: "translateX(-50%)" } : undefined}
        hideCloseButton
        onClick={() => onOpenChange(false)}
      >
        <DialogTitle className="sr-only">Creative Gallery Preview</DialogTitle>
        <DialogDescription className="sr-only">
          Gallery view of creative content with images and videos. Click outside to close.
        </DialogDescription>
        {open && loading ? (
          <div
            className="flex items-center justify-center w-full h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <WaveLoader className="w-[100px] opacity-70" />
          </div>
        ) : open ? (
          <>
            {enableSearch && isSearching && (
              <div
                className="squircle squircle-fill-[white] squircle-smooth-[1] squircle-radius-[8px] w-[400px] flex items-center h-8 py-2 px-3 pr-2 mt-4 mb-3 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                style={{
                  position: "relative",
                  left:
                    "calc(50% - " + (gridCols * 295 + (gridCols - 1) * 14) / 2 + "px)",
                }}
              >
                <div className="flex items-center w-full">
                  <RiSearchLine size={14} className="shrink-0" color="#9aa3bd" />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search Ad name"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    className="flex-1 bg-transparent border-none outline-none text-gray-700 placeholder-gray-400 text-[12px] translate-y-[1px] ml-[6px]"
                  />
                </div>

                <button
                  className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors ml-auto"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsSearching(false);
                    setSearchQuery("");
                  }}
                >
                  <RiCloseLine size={14} color="#9aa3bd" className="shrink-0" />
                </button>
              </div>
            )}
            <div className="relative w-full">
              {enableSearch && !isSearching && (
                <div
                  className="absolute top-4 z-50"
                  style={{
                    left:
                      "calc(50% - 40px - " +
                      (gridCols * 295 + (gridCols - 1) * 14) / 2 +
                      "px)",
                  }}
                >
                  <SqButton
                    variant={"icon"}
                    bgColor="white"
                    className="h-8 w-8"
                    hasOutline
                    hoverBgColor="#F6F6F6"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSearching(true);
                    }}
                  >
                    <RiSearchLine size={14} color="#9AA3BD" className="shrink-0" />
                  </SqButton>
                </div>
              )}
              <div
                ref={scrollContainerRef}
                className={`w-full max-h-[100vh] overflow-y-auto scrollbar-hide ${enableSearch && !isSearching ? "py-4" : ""} ${enableSearch && isSearching ? "pb-[76px]" : ""}`}
                onScroll={handleScroll}
              >
                <div className="flex justify-center w-full" ref={containerRef}>
                  <div
                    className="grid gap-[14px] mx-auto"
                    style={{
                      gridTemplateColumns: `repeat(${columnData.length}, 295px)`,
                      width: "fit-content",
                    }}
                  >
                    {columnData.map((column, colIndex) => (
                      <div key={colIndex} className="w-min flex flex-col gap-[14px]">
                        {column.map((item, rowIndex) => {
                          const winningCriteria = getWinningCriteriaForCreative(
                            item as unknown as Record<string, unknown>,
                          );

                          return (
                            <div
                              key={`${colIndex}-${rowIndex}`}
                              ref={colIndex === 0 && rowIndex === 0 ? cardRef : undefined}
                              className="w-[295px] h-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <AdCard
                                winningCriteria={winningCriteria}
                                header={{
                                  title: item.account_name || "",
                                  icon: item.account_icon_url,
                                }}
                                body={{
                                  icon_src: getPlatformIcon(
                                    item.platform ?? Platform.Facebook,
                                  ),
                                  name: item.ad_name || "",
                                  description: item.bodies?.[0] || "",
                                  media: {
                                    source: item?.videos?.[0]
                                      ? item?.videos?.[0]?.permalink_url
                                      : item?.images?.[0]?.permalink_url,
                                    type: item?.videos?.[0] ? "video" : "image",
                                    thumbnail: item.thumbnail_url,
                                    videoRetentionData: item?.videos?.[0]
                                      ? item.videoRetentionData
                                      : undefined,
                                    actions: [
                                      {
                                        component: (
                                          <OptionsAction
                                            url={
                                              item?.videos?.[0]
                                                ? item?.videos?.[0]?.permalink_url?.toLowerCase?.() ===
                                                  "n/a"
                                                  ? null
                                                  : item?.videos?.[0]?.permalink_url
                                                : (
                                                      item?.images?.[0]
                                                        ?.uncompressed_url ||
                                                      item?.images?.[0]?.permalink_url
                                                    )?.toLowerCase?.() === "n/a"
                                                  ? null
                                                  : item?.images?.[0]?.uncompressed_url ||
                                                    item?.images?.[0]?.permalink_url
                                            }
                                            ad_id={item.ad_id || ""}
                                            accountId={accountId || ""}
                                            accountName={item.account_name || ""}
                                            fbPostUrl={
                                              item?.fb_post_url?.toLowerCase() === "n/a"
                                                ? null
                                                : item?.fb_post_url
                                            }
                                            igPostUrl={
                                              item?.ig_post_url?.toLowerCase() === "n/a"
                                                ? null
                                                : item?.ig_post_url
                                            }
                                            tiktokPostUrl={
                                              item?.tiktok_post_url?.toLowerCase() ===
                                              "n/a"
                                                ? null
                                                : item?.tiktok_post_url
                                            }
                                            type={item?.videos?.[0] ? "video" : "image"}
                                            labelManagementProps={
                                              groupType === "text_ai_label" &&
                                              !disableLabelManagement
                                                ? {
                                                    uniqueLabels:
                                                      labelData?.unique_labels || [],
                                                    currentLabelItems:
                                                      getCurrentLabelItems(
                                                        item.ad_id || "",
                                                      ),
                                                    onLabelSelect: (labelItem) =>
                                                      void handleLabelsUpdate(
                                                        item.ad_id || "",
                                                        [labelItem],
                                                      ),
                                                    onLabelsSave: (labelItems) =>
                                                      void handleLabelsUpdate(
                                                        item.ad_id || "",
                                                        labelItems,
                                                      ),
                                                    labelView: labelView,
                                                    creativeKind: item.type,
                                                  }
                                                : null
                                            }
                                          />
                                        ),
                                        position: "top-right",
                                        visibility: "hover",
                                      },
                                    ],
                                  },
                                }}
                                footer={{
                                  cta_label: item.call_to_action_titles?.[0] || "",
                                  landing_page: item.landing_pages?.[0] || "",
                                  title: item.titles?.[0] || "",
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                {apiPaginationMode && isLoadingMore && (
                  <div
                    className="flex justify-center py-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <WaveLoader className="w-[100px] opacity-70" />
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CreativeGalleryPreview;
